import { useUIStore } from '@/config/stores/ui-store';
import ConversationList from '@/features/conversation/components/conversation-list/conversation-list';
import type { Conversation } from '@/features/conversation/models/conversation';
import type { ConversationSearchItem } from '@/features/conversation/models/conversation-dto';
import { useConversationSearch } from '@/features/conversation/queries/conversation.queries';
import { useInvalidateMessages } from '@/features/message-list';
import { UserList } from '@/features/participant/components/user-list/user-list';
import SearchField from '@/shared/components/search-field';
import { AddOutlined } from '@mui/icons-material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Box, Button, Drawer as MuiDrawer, Stack, Tab, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversationCreateDirectListener } from '../../hooks/use-conversation-create-direct-listener';
import { ConversationCreateDialog } from '../conversation-add-dialog';

import { chatEE } from '@/layout/events/chat-event-emitter';
import { DRAWER_COLLAPSED_WIDTH, DRAWER_WIDTH } from '@/layout/layout.constant';
import { PermissionConfigDialog } from '../conversation-member/permission-config-dialog';
import DrawerHeader from './conversation-drawer.header';
import { ConversationSearchResults } from './conversation-drawer.search';

interface ConversationDrawerProps {
  open: boolean;
  onClose: () => void;
  container: any;
}

export const ConversationDrawer = ({ open, onClose, container }: ConversationDrawerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = open ? DRAWER_WIDTH : DRAWER_COLLAPSED_WIDTH;
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const { activeConversation, setActiveConversation } = useUIStore();

  const invalidateMessage = useInvalidateMessages();

  // Remote search
  const isRemoteSearchActive = searchTerm.length >= 3 && activeTab === 0;
  const {
    data: searchData,
    fetchNextPage: fetchNextSearchPage,
    hasNextPage: hasNextSearchPage,
    isFetching: isSearchFetching,
    isLoading: isSearchLoading,
    error: searchError
  } = useConversationSearch(searchTerm, isRemoteSearchActive);

  // Actions
  useConversationCreateDirectListener();

  const onSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setSearchTerm('');
    }
  }, []);

  const handleConvSel = useCallback(
    async (conversation: Conversation | ConversationSearchItem) => {
      // Type guard to check if it's a search item
      const isSearchItem = (conv: Conversation | ConversationSearchItem): conv is ConversationSearchItem => {
        return 'id' in conv;
      };

      const convId = conversation.conversationId;

      if (activeTab === 0 && convId !== activeConversation?.conversationId) {
        let conv: Conversation;

        if (isSearchItem(conversation)) {
          // Fetch full conversation details from remote
          try {
            const { getConversationById } = await import('@/features/conversation/services/conversation.service');
            conv = await getConversationById(conversation.conversationId);
          } catch (error) {
            console.error('Failed to fetch conversation:', error);
            return;
          }
        } else {
          conv = conversation;
        }

        invalidateMessage(convId!);
        setActiveConversation(conv);
        if (isMobile) {
          onClose();
        }
      }
    },
    [activeTab, activeConversation?.conversationId, invalidateMessage, setActiveConversation, isMobile, onClose]
  );

  const tabContextValue = useMemo(() => (open ? activeTab : 0), [open, activeTab]);

  const drawerContent = (
    <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
      <DrawerHeader open={open} isMobile={isMobile} />
      {open && (
        <Stack direction='column' gap={1} sx={{ p: 1 }}>
          <SearchField
            className='mex:min-w-0 mex:flex-1'
            value={searchTerm}
            onChange={onSearch}
            delay={300}
            placeholder={
              activeTab === 0 ? t('conversations.drawer.searchConversations') : t('conversations.drawer.searchUsers')
            }
          />
          <Button
            variant='contained'
            color='primary'
            size='medium'
            startIcon={<AddOutlined />}
            onClick={() => chatEE.emit('conversation-add')}
          >
            {t('conversations.create.button')}
          </Button>
        </Stack>
      )}
      <TabContext value={tabContextValue}>
        {open && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }} component='div'>
            <TabList onChange={handleTabChange}>
              <Tab label={t('conversations.tabs.conversations')} />
              <Tab label={t('conversations.tabs.contacts')} />
            </TabList>
          </Box>
        )}
        <TabPanel keepMounted value={0} sx={{ px: 0, flex: 1, overflow: 'hidden', paddingY: 1, position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              marginTop: 1,
              opacity: isRemoteSearchActive ? 0 : 1,
              transition: 'opacity 200ms ease-in-out',
              pointerEvents: isRemoteSearchActive ? 'none' : 'auto'
            }}
          >
            <ConversationList
              searchQuery={isRemoteSearchActive ? '' : searchTerm}
              conversationId={activeConversation?.conversationId}
              onSelect={handleConvSel}
            />
          </Box>
          {isRemoteSearchActive && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: 1,
                transition: 'opacity 200ms ease-in-out'
              }}
            >
              <ConversationSearchResults
                query={searchTerm}
                conversationId={activeConversation?.conversationId}
                onSelect={handleConvSel}
                searchData={searchData}
                fetchNextPage={fetchNextSearchPage}
                hasNextPage={hasNextSearchPage}
                isFetching={isSearchFetching}
                isLoading={isSearchLoading}
                error={searchError}
              />
            </Box>
          )}
        </TabPanel>
        <TabPanel value={1} sx={{ p: 0, flex: 1, overflow: 'hidden', paddingY: 1 }}>
          <UserList
            searchTerm={searchTerm}
            onUserSelect={(u) => {
              chatEE.emit('conversation-create-direct', { user: u! });
            }}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );

  return (
    <MuiDrawer
      container={container || undefined}
      variant={isMobile ? 'temporary' : 'permanent'}
      open={open}
      onClose={onClose}
      hideBackdrop={!isMobile}
      sx={{
        width: drawerWidth,
        transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: isMobile ? 50 : 10,
        '& .MuiDrawer-paper': {
          position: 'absolute',
          top: 0,
          left: 0,
          width: drawerWidth,
          minWidth: 0,
          height: '100%',
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          })
        }
      }}
      ModalProps={{
        container: container ? () => container : undefined,
        disablePortal: true,
        keepMounted: true,
        disableScrollLock: true,
        disableAutoFocus: true,
        disableEnforceFocus: true
      }}
    >
      {drawerContent}
      <ConversationCreateDialog />
      <PermissionConfigDialog />
    </MuiDrawer>
  );
};
